import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Navigate, Link, useSearchParams } from "react-router-dom";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EnvelopeSimple, Lock, User, ArrowRight } from "@phosphor-icons/react";

export default function LoginPage() {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Pre-fill from a client-invite email link: ?signup=1&email=foo@bar.com
  // signup=1 flips the form to register-mode so the client just picks a password.
  const [isLogin, setIsLogin] = useState(searchParams.get("signup") !== "1");
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // If the URL params change (e.g. user opens another invite link in same
  // tab) keep the form in sync.
  useEffect(() => {
    const next = searchParams.get("email") || "";
    if (next) setEmail(next);
    if (searchParams.get("signup") === "1") setIsLogin(false);
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setSubmitting(true);
    try {
      let u;
      if (isLogin) {
        u = await login(email, password);
      } else {
        u = await register(email, password, name, "client", searchParams.get("token") || undefined);
      }
      // Referrers logging in via firm sign-in get bounced to their portal.
      if (u?.role === "referrer" && u.referrer_token) {
        navigate(`/referrer-dashboard/${u.referrer_token}`);
      } else {
        navigate("/");
      }
    } catch (err) {
      // Prefer a server-supplied `detail` (e.g. "Invalid email or password",
      // "Too many login attempts..."). Fall back to a status-aware message so
      // users see why login actually failed instead of a vague banner.
      const detail = err.response?.data?.detail;
      // 409 on sign-up = passwordless client; backend has emailed a one-click
      // sign-in link. Show it as friendly guidance, not a red error.
      if (err.response?.status === 409 && detail) {
        setInfo(formatApiErrorDetail(detail));
      } else if (detail != null) {
        setError(formatApiErrorDetail(detail));
      } else if (err.response) {
        // Server responded but with no usable body (5xx, CORS-stripped, HTML).
        setError(`Login failed (server error ${err.response.status}). Please try again in a moment.`);
      } else if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
        setError("Can't reach the server. Check your connection and try again.");
      } else {
        setError(err.message || "Login failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Redirect if already authenticated
  if (user) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left panel — branded hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ background: '#100f5d' }}>
        {/* Subtle architectural backdrop @ low opacity */}
        <img
          src="https://images.pexels.com/photos/3062948/pexels-photo-3062948.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-15 mix-blend-luminosity"
        />
        {/* Vertical navy gradient overlay */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(16,15,93,0.95) 0%, rgba(10,9,69,0.98) 100%)' }} />
        {/* Copper accent bar */}
        <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: '#c87d3a' }} />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center">
            <img src="/logo.png" alt="UKC Legal Solicitors" className="h-16 w-auto" />
          </div>

          <div className="max-w-md">
            <div className="inline-flex items-center gap-2 mb-5 px-3 py-1 rounded-full" style={{ background: 'rgba(200,125,58,0.15)', border: '1px solid rgba(200,125,58,0.4)' }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#c87d3a' }} />
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#e89e63' }}>Conveyancing case management</span>
            </div>
            <h1
              className="text-4xl xl:text-5xl font-bold text-white leading-[1.1] mb-5"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Fast and affordable<br />conveyancing services
            </h1>
            <p className="text-slate-300 text-base leading-relaxed mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Track every stage of your transaction in real time. From instruction to completion, kept simple by a team that's been doing this for over 50 years.
            </p>

            {/* Trust strip */}
            <div className="grid grid-cols-3 gap-4 max-w-sm pt-6 border-t border-white/10">
              <div>
                <div className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>50+</div>
                <div className="text-[11px] text-slate-400 uppercase tracking-wider">years experience</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>4–10<span className="text-base"> wks</span></div>
                <div className="text-[11px] text-slate-400 uppercase tracking-wider">avg completion</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>5<span className="text-base">★</span></div>
                <div className="text-[11px] text-slate-400 uppercase tracking-wider">rated nationwide</div>
              </div>
            </div>
          </div>

          <div className="text-slate-400 text-xs space-y-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <p className="font-semibold text-slate-300">UKC Legal · Norwich</p>
            <p>38 Prince of Wales Road, Norwich NR1 1LG · (01603) 339174</p>
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile logo (navy bg pill so the white logo is visible on white screen) */}
          <div className="lg:hidden flex items-center justify-center mb-8 p-4 rounded-lg" style={{ background: '#100f5d' }}>
            <img src="/logo.png" alt="UKC Legal Solicitors" className="h-10 w-auto" />
          </div>

          <h2
            className="text-2xl sm:text-3xl font-bold mb-2"
            style={{ fontFamily: 'Outfit, sans-serif', color: '#100f5d' }}
          >
            {isLogin ? "Welcome back" : "Create your account"}
          </h2>
          <p className="text-slate-500 mb-8 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {isLogin
              ? "Sign in to view your conveyancing case"
              : "Get started with UKC Legal today"}
          </p>

          {info && (
            <div
              className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-sm"
              data-testid="auth-info"
            >
              {info}
            </div>
          )}
          {error && (
            <div
              className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
              data-testid="auth-error"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="auth-form">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-slate-700">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="name"
                    data-testid="register-name-input"
                    placeholder="John Smith"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 h-11 bg-white border-slate-200 focus:border-[#100f5d] focus:ring-[#100f5d]/20"
                    required={!isLogin}
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email</Label>
              <div className="relative">
                <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  data-testid="email-input"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11 bg-white border-slate-200 focus:border-[#100f5d] focus:ring-[#100f5d]/20"
                  required
                />
              </div>
            </div>

          <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">Password</Label>
                {isLogin && (
                  <button type="button" onClick={() => navigate(`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ""}`)}
                    className="text-xs font-semibold hover:underline" style={{ color: "#100f5d" }} data-testid="forgot-password-link">
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  data-testid="password-input"
                  placeholder="Min 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-11 bg-white border-slate-200 focus:border-[#100f5d] focus:ring-[#100f5d]/20"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              data-testid="auth-submit-btn"
              disabled={submitting}
              className="w-full h-11 text-white font-medium rounded-lg transition-colors"
              style={{ background: '#100f5d' }}
              onMouseDown={(e) => (e.currentTarget.style.background = '#0a0945')}
              onMouseUp={(e) => (e.currentTarget.style.background = '#100f5d')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#100f5d')}
            >
              {submitting ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <span className="flex items-center justify-center gap-2">
                  {isLogin ? "Sign in" : "Create account"}
                  <ArrowRight weight="bold" className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>

          {/* Magic-link option — clients can request a passwordless
              sign-in email if they've forgotten their password or just
              prefer not to type one. Backend silently no-ops on unknown
              emails so we don't leak account existence. */}
          {isLogin && (
            <div className="mt-4 text-center">
              <button
                type="button"
                data-testid="magic-link-request-btn"
                onClick={async () => {
                  setError("");
                  if (!email) { setError("Enter your email above first"); return; }
                  try {
                    const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/client/request-magic-link`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ email }),
                    });
                    const data = await resp.json().catch(() => ({}));
                    setError("");
                    alert(data.message || "If your email is on file, a sign-in link is on the way.");
                  } catch (e) {
                    setError("Couldn't send the link. Please try again.");
                  }
                }}
                className="text-xs text-slate-500 hover:underline"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Or email me a sign-in link instead
              </button>
            </div>
          )}

          <p className="mt-6 text-center text-sm text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
            <button
              type="button"
              data-testid="toggle-auth-mode"
              onClick={() => { setIsLogin(!isLogin); setError(""); }}
              className="font-semibold hover:underline"
              style={{ color: '#100f5d' }}
            >
              {isLogin ? "Sign up" : "Sign in"}
            </button>
          </p>

          <p className="mt-3 text-center text-xs text-slate-400" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Referrer? <Link to="/referrer-login" className="font-semibold hover:underline" style={{ color: '#100f5d' }} data-testid="referrer-login-link">Sign in here</Link>
          </p>

          <p className="mt-8 text-center text-[11px] text-slate-400" style={{ fontFamily: 'Manrope, sans-serif' }}>
            UKC Legal · part of the DJF Solicitors group
          </p>
        </div>
      </div>
    </div>
  );
}
